/**
 * Desktop-only per-screen header bar — shared geometry constants.
 *
 * The bar's height itself is `var(--header-height)` (index.css); these are
 * the layout-algorithm numbers the bar's own fit logic depends on, named
 * once here rather than repeated as bare literals wherever they're read.
 */

/** The screen name never shrinks below this width — tools give way first. */
export const SCREEN_HEADER_NAME_MIN_WIDTH = 180

/**
 * The name's last floor, below `SCREEN_HEADER_NAME_MIN_WIDTH`. With every
 * ordinary rung spent the name truncates past its floor; at this width the
 * bar takes its final rung (the action's label — `actionCompact`) rather than
 * let the name reach nothing. Measured need: the deck screen at 768–850px with
 * the sidebar open, where the name reached 0px and, in ru, ⋯ was pushed 29px
 * off the bar (2026-09-23).
 */
export const SCREEN_HEADER_NAME_NARROW_FLOOR = 96

/** Gap between the bar's zones (back / identity / slack / controls). */
export const SCREEN_HEADER_GAP = 16

/** Gap between the members of the controls group (lead / action / tools),
 * and between the controls inside each. */
export const SCREEN_HEADER_CONTROLS_GAP = 8

/**
 * The minimum slack between the bar's two ends (DESIGN_SYSTEM.md §10.3's
 * `--header-zone-gap`) — the identity and the controls never touch, even at
 * the width where the identity is at its floor.
 *
 * Declared here rather than as a CSS custom property because the fit check
 * has to SUBTRACT it: the slack is a real element with this as its
 * `min-width`, so it takes its share before the identity gets what is left.
 * A check that did not know about it would measure an identity 32px wider
 * than the one the browser renders — which is exactly how the floor came to
 * be breached at 800px on the deck screen after the slack was introduced.
 */
export const SCREEN_HEADER_ZONE_GAP = 32

/** Slack required before the bar is allowed to step back UP its degrade
 * ladder — avoids the rungs flapping right at the boundary. */
export const SCREEN_HEADER_UPGRADE_HYSTERESIS = 32

/**
 * **`SCREEN_HEADER_MAX_TOOLS` was here, and is removed (2026-09-12).**
 *
 * It declared "no screen's tools should exceed four interactive targets" and
 * was read by nothing — its own comment said so. DESIGN_SYSTEM.md §8.5 records
 * that as found: an intention wearing a constant's clothes, which is worse
 * than no constant, because it reads like a guarantee to anyone who greps for
 * one. The instruction was to make it enforced or remove it.
 *
 * **Removed, not enforced**, because neither thing this module could do is
 * enforcement:
 *
 *  • *Refuse the fifth target.* The bar would have to drop a screen's controls
 *    at runtime — in production, on a screen whose author already shipped
 *    them. A rule whose sanction is "the reader loses a control they need" is
 *    not a safer version of the crowding it guards against.
 *  • *Count and warn.* The count is not available to count. `tools` is an
 *    opaque `ReactNode`; the only way to a number is to query the rendered
 *    DOM, and that DOM does not distinguish a control from an OPEN OVERFLOW
 *    MENU's items — the deck screen's `···` renders fifteen `menuitem`s inside
 *    the tools node the moment it is opened. A check that fires on the wrong
 *    thing teaches everyone to ignore it, and that is how a real violation
 *    gets waved through.
 *
 * Four targets is a composition judgement made when a screen's header content
 * is designed, and it stays where such judgements are made and can be checked
 * against the rendered bar: DESIGN_SYSTEM.md §8.5 and §10, with the measured
 * per-screen counts beside it. Nothing is lost by the constant's removal that
 * the constant was providing.
 */
